import PropTypes from "prop-types";
import { memo } from "react";

// Common Sections
import CallActivityPerformanceComponent from "@PayPlans/components/Commons/CallActivityPerformance";
import CustomerSatisfactionComponent from "@PayPlans/components/Commons/CustomerSatisfaction";
import ExcellenceMakesComponent from "@PayPlans/components/OEMs/ExcellenceMakes";
import LongevityComponent from "@PayPlans/components/Commons/Longevity";
import OperatingCostsComponent from "@PayPlans/components/Commons/OperatingCosts";
import VolumeBonusesComponent from "@/PayPlans/components/Commons/VolumeBonuses";
import VariableOperationsGpsBonusComponent from "@PayPlans/components/Commons/VariableOperationsGpsBonus";

// Customs
import DefaultCommissionsComponent from "@/PayPlans/components/Defaults/Managers/Sales/DefaultCommissions";
import CrossStoreCommissionsComponent from "@/PayPlans/components/Defaults/Managers/Sales/CrossStoreCommissions";
import GrowthBonusComponent from "@/PayPlans/components/Defaults/Managers/Sales/GrowthBonusMakes";
import MarketShareComponent from "@/PayPlans/components/OEMs/MarketShareMakes";
import PinnacleComponent from "@/PayPlans/components/OEMs/Pinnacle";
import RankingBonusComponent from "@/PayPlans/components/Defaults/Managers/Sales/RankingBonusMakes";

function CallActivityPerformanceSection(props) {
	return <CallActivityPerformanceComponent {...props} sectionId={"callActivityPerformance"} />;
}
CallActivityPerformanceSection.propTypes = {
	sectionId: PropTypes.string,
	performance: PropTypes.object,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};

function CrossStoreCommissionsSection(props) {
	return <CrossStoreCommissionsComponent {...props} />;
}
CrossStoreCommissionsSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		newVehicles: PropTypes.shape({
			commissions: PropTypes.object,
			miniDeal: PropTypes.array,
			variableGrossStructure: PropTypes.array,
		}),
		preOwned: PropTypes.shape({
			commissions: PropTypes.object,
			miniDeal: PropTypes.array,
			variableGrossStructure: PropTypes.array,
		}),
		finance: PropTypes.shape({
			commissions: PropTypes.array,
			variableGrossStructure: PropTypes.array,
			globalUnitCounts: PropTypes.bool,
		}),
		globalUnitCounts: PropTypes.bool,
	}),
	brands: PropTypes.shape({
		newVehicles: PropTypes.array,
		preOwned: PropTypes.array,
	}),
	errors: PropTypes.array,
	loading: PropTypes.bool,
	loadingMakes: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};

function CustomerSatisfactionSection(props) {
	const {
		sectionId = "customerSatisfaction",
		isService = false,
		content = {
			makeMetrics: [],
			weighting: {
				weightingType: "UNITS",
				customWeights: [],
			},
		},
		excludesMakes = ["Cadillac"],
		errors = [],
		useOnlyIndividualMetrics = false,
		useOnlyStoreMetrics = true,
	} = props;
	return (
		<CustomerSatisfactionComponent
			{...props}
			sectionId={sectionId}
			isService={isService}
			content={content}
			excludesMakes={excludesMakes}
			errors={errors}
			useOnlyIndividualMetrics={useOnlyIndividualMetrics}
			useOnlyStoreMetrics={useOnlyStoreMetrics}
		/>
	);
}
CustomerSatisfactionSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		makeMetrics: PropTypes.array,
		weighting: PropTypes.shape({
			weightingType: PropTypes.string,
			customWeights: PropTypes.array,
		}),
	}),
	excludesMakes: PropTypes.array,
	errors: PropTypes.array,
	isService: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useOnlyIndividualMetrics: PropTypes.bool,
	useOnlyStoreMetrics: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};

function LongevitySection(props) {
	const { sectionId = "longevity", content = [] } = props;
	return <LongevityComponent {...props} sectionId={sectionId} content={content} />;
}
LongevitySection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};

function DefaultCommissionsSection(props) {
	const {
		sectionId = "default",
		content = {
			commissions: [],
			preOwned: null,
			newAndPreOwnedRequired: false,
			combineFranchises: false,
			globalUnitCounts: false,
			isGuaranteedDraw: false,
		},
		franchises = [],
		oems = [],
	} = props;
	return <DefaultCommissionsComponent {...props} sectionId={sectionId} content={content} franchises={franchises} oems={oems} />;
}
DefaultCommissionsSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.object,
	franchises: PropTypes.array,
	oems: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};

function PinnacleSection(props) {
	const {
		sectionId = "pinnacle",
		content = {
			metrics: [],
			tiers: [],
		},
	} = props;
	return <PinnacleComponent {...props} sectionId={sectionId} content={content} />;
}
PinnacleSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		metrics: PropTypes.array,
		numberRequired: PropTypes.number,
		attained: PropTypes.shape({
			input: PropTypes.string,
			value: PropTypes.number,
		}),
		missed: PropTypes.shape({
			input: PropTypes.string,
			value: PropTypes.number,
		}),
	}),
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

function RankingBonusSection(props) {
	const { sectionId = "rankingBonus", content = null, franchises = [] } = props;
	return <RankingBonusComponent {...props} sectionId={sectionId} content={content} franchises={franchises} />;
}
RankingBonusSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		makes: PropTypes.array,
		simultaneousRanking: PropTypes.bool,
	}),
	franchises: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

function VolumeBonusSection(props) {
	const {
		sectionId = "volumeBonus",
		position = "",
		content = {
			bonuses: [],
			preOwned: null,
			combined: null,
			newAndPreOwnedRequired: false,
			combineFranchises: false,
			globalUnitCounts: false,
		},
		franchises = [],
	} = props;
	return <VolumeBonusesComponent {...props} sectionId={sectionId} position={position} content={content} franchises={franchises} />;
}
VolumeBonusSection.propTypes = {
	sectionId: PropTypes.string,
	position: PropTypes.string,
	content: PropTypes.object,
	franchises: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

function MarketShareSection(props) {
	const { sectionId = "marketShare", content = null, excludesMakes = [] } = props;
	return <MarketShareComponent {...props} sectionId={sectionId} content={content} excludesMakes={excludesMakes} />;
}
MarketShareSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		makes: PropTypes.array,
		simultaneousMarketShare: PropTypes.bool,
	}),
	excludesMakes: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

function SFESection(props) {
	const {
		sectionId = "sfe",
		reference = "SFE",
		department = "SALES",
		content = {
			makeMetrics: [],
		},
	} = props;
	return <ExcellenceMakesComponent {...props} sectionId={sectionId} reference={reference} department={department} content={content} />;
}
SFESection.propTypes = {
	sectionId: PropTypes.string,
	reference: PropTypes.string,
	department: PropTypes.string,
	content: PropTypes.shape({
		makeMetrics: PropTypes.array,
	}),
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};

function GrowthSection(props) {
	const {
		sectionId = "growthBonus",
		content = {
			makes: [],
			preOwned: null,
			newAndPreOwnedRequired: false,
			combineFranchises: false,
		},
		oems = [],
		franchises = [],
	} = props;
	return <GrowthBonusComponent {...props} sectionId={sectionId} content={content} oems={oems} franchises={franchises} />;
}
GrowthSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		makes: PropTypes.array,
		preOwned: PropTypes.object,
		newAndPreOwnedRequired: PropTypes.bool,
		combineFranchises: PropTypes.bool,
	}),
	oems: PropTypes.array,
	franchises: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

function OperatingCostsSection(props) {
	const {
		sectionId = "operatingCosts",
		content = {
			makes: [],
			combineOEMs: false,
		},
		oems = [],
	} = props;
	return <OperatingCostsComponent {...props} sectionId={sectionId} content={content} oems={oems} />;
}
OperatingCostsSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		makes: PropTypes.array,
		combineOEMs: PropTypes.bool,
	}),
	oems: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};

const VariableOperationsGpsBonusSection = (props) => {
	return <VariableOperationsGpsBonusComponent {...props} />;
}

VariableOperationsGpsBonusSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.object,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

const CallActivityPerformance = memo(CallActivityPerformanceSection);
const CrossStoreCommissions = memo(CrossStoreCommissionsSection);
const CustomerSatisfaction = memo(CustomerSatisfactionSection);
const DefaultCommissions = memo(DefaultCommissionsSection);
const Longevity = memo(LongevitySection);
const Pinnacle = memo(PinnacleSection);
const RankingBonus = memo(RankingBonusSection);
const VolumeBonus = memo(VolumeBonusSection);
const SFEBonus = memo(SFESection);
const GrowthBonus = memo(GrowthSection);
const MarketShare = memo(MarketShareSection);
const OperatingCosts = memo(OperatingCostsSection);
const VariableOperationsGpsBonus = memo(VariableOperationsGpsBonusSection);

export {
	CallActivityPerformance,
	CrossStoreCommissions,
	CustomerSatisfaction,
	DefaultCommissions,
	Longevity,
	Pinnacle,
	RankingBonus,
	VolumeBonus,
	SFEBonus,
	MarketShare,
	GrowthBonus,
	OperatingCosts,
	VariableOperationsGpsBonus,
};
